/**
 * Shift component logger - watches specific components for insert/resize events
 * @module lib/shiftComponentLogger
 */

const DEFAULT_SELECTORS = [
  '.rm-embed-container.rm-embed-instagram',
  '#relatedsearches1',
  '.ad-wrapper',
  '.stories-player-container'
];

const DEFAULT_OPTIONS = {
  enabled: false,
  selectors: DEFAULT_SELECTORS,
  logInsert: true,
  logResize: true,
  minDelta: 40
};

let initialized = false;

function buildNodeSelector(node) {
  if (!node || !node.tagName) return null;
  let selector = node.tagName.toLowerCase();
  if (node.id) {
    selector += `#${node.id}`;
  }
  if (node.classList && node.classList.length) {
    const classes = Array.from(node.classList).slice(0, 3);
    if (classes.length) {
      selector += `.${classes.join('.')}`;
    }
  }
  return selector;
}

function initShiftComponentLogger(options = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { enabled: false, reason: 'unsupported' };
  }
  if (initialized) {
    return window.__millaShiftWatch || { enabled: true };
  }

  const enabled = options.enabled === true || window._millaLogShiftWatch === true;
  if (!enabled) {
    return { enabled: false, reason: 'disabled' };
  }

  const config = {
    ...DEFAULT_OPTIONS,
    ...options,
    enabled: true
  };

  const selectors = Array.isArray(config.selectors) && config.selectors.length
    ? config.selectors
    : DEFAULT_SELECTORS;

  const observed = new WeakSet();

  const log = (...args) => {
    if (window.console?.info) {
      
    }
  };

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const node = entry.target;
        if (!node) return;
        const rect = entry.contentRect || node.getBoundingClientRect();
        const prev = node.__millaShiftWatchLast;
        const prevHeight = prev && typeof prev.height === 'number' ? prev.height : 0;
        const delta = rect.height - prevHeight;
        node.__millaShiftWatchLast = { height: rect.height, width: rect.width };

        if (!config.logResize) return;
        if (Math.abs(delta) < config.minDelta) return;

        log('resize', {
          selector: buildNodeSelector(node),
          height: Math.round(rect.height),
          width: Math.round(rect.width),
          delta: Math.round(delta),
          time: Math.round(performance.now())
        });
      });
    })
    : null;

  function trackNode(node, reason) {
    if (!node || observed.has(node)) return;
    observed.add(node);

    const rect = node.getBoundingClientRect();
    node.__millaShiftWatchLast = { height: rect.height, width: rect.width };

    if (config.logInsert) {
      log('insert', {
        selector: buildNodeSelector(node),
        height: Math.round(rect.height),
        width: Math.round(rect.width),
        reason
      });
    }

    if (resizeObserver) {
      try {
        resizeObserver.observe(node);
      } catch (_) {
        // ignore observer failures
      }
    }
  }

  function collectMatches(root, reason) {
    if (!root || root.nodeType !== 1) return;
    const element = root;
    selectors.forEach((selector) => {
      if (element.matches && element.matches(selector)) {
        trackNode(element, reason);
      }
      if (element.querySelector) {
        const matches = element.querySelectorAll(selector);
        matches.forEach((match) => trackNode(match, reason));
      }
    });
  }

  // Track existing matches
  collectMatches(document.documentElement || document.body, 'init');

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        collectMatches(node, 'mutation');
      });
    });
  });

  try {
    observer.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true
    });
  } catch (_) {
    // ignore observer failures
  }

  const state = {
    enabled: true,
    selectors,
    logInsert: config.logInsert,
    logResize: config.logResize,
    minDelta: config.minDelta
  };

  window.__millaShiftWatch = state;
  initialized = true;
  return state;
}

module.exports = { initShiftComponentLogger };
